import { html, ifDefined, SYSTableDefIds, TemplateResult } from "../../"
import type {
  _TableGroupColumnsToSetType,
  HookTableGroupHeadersFooters_Type,
  HookViewRowClass_Type,
} from "../../global/hooks"
import {
  HookTableGroupFooters,
  HookTableGroupHeaders,
  HookViewRowClass,
} from "../../global/hooks"

import type { SYSFormState } from "../forms/actions"
import { FormHasVisibleSummaryErrors } from "../forms/formapi"

import { SYSBaseElement } from "../sysbaseelement"
import { _GetAlign } from "./helpers"
import type { NavProps } from "./rowcontrol"
import {
  EditRowDef,
  EditRowSaveCancelDef,
  GroupBreakType,
  OvlSysColumn,
  SelectedEditRow,
  SelectedRow,
  SelectedViewRow,
  SYSTableData,
  SYSTableDef,
  ViewRowClassType,
} from "./table"
import { TableRebuild } from "./tableapi"
export let _HookTableGroupDefId: SYSTableDefIds
export type GroupHeaderFooterBreaksValueType = "columns" | "custom"

export type TableRowDef = {
  data: SYSTableData
  selected: SelectedRow
  tableDef: SYSTableDef
  processesToTrackForRowFnHintDisplay: any
  thHeaderEnd: {}
  key: string
  index: number
  intersectionObserver: IntersectionObserver
  tableGroupHeaderBreak: {
    isBreak?: boolean
    isFirstRow?: boolean
    isLastRow: boolean
    groups?: { [key: string]: GroupBreakType }
    groupAccessStrings?: string[]
  }
  tableGroupFooterBreak: {
    isBreak?: boolean
    isFirstRow?: boolean
    isLastRow: boolean
    groups?: { [key: string]: GroupBreakType }
    groupAccessStrings?: string[]
  }
}

export type TableRowDataDef = {
  row: {}
  tableDef: SYSTableDef
  key: string
  thHeaderEnd: {}
  intersectionObserver: IntersectionObserver
  processesToTrackForRowFnHintDisplay: any
}

export class SYSTableRowWrapper extends SYSBaseElement {
  props: any
  row: TableRowDef
  columnsVisible: {}
  classToSet: string

  async init() {
    this.row = this.props()
    await super.init()
  }

  getTemplate(
    type: GroupHeaderFooterBreaksValueType,
    columnsToSet?: _TableGroupColumnsToSetType
  ):
    | {
        cssClassToSet: string
        _type: GroupHeaderFooterBreaksValueType
        valueToSet: _TableGroupColumnsToSetType
      }
    | {
        cssClassToSet: string
        _type: GroupHeaderFooterBreaksValueType
        valueToSet: TemplateResult
      } {
    if (type === "columns") {
      return { cssClassToSet: "", _type: type, valueToSet: columnsToSet }
    } else {
      return {
        cssClassToSet: "",
        _type: type,
        valueToSet: undefined,
      }
    }
  }

  async getUI() {
    let def = this.row.tableDef
    let key = this.row.key
    let data = this.row.data
    let rows = data.data
    let row = rows[key]
    if (!row) {
      return null
    }
    let colSysState = data.data[key].OvlSysState as OvlSysColumn
    let editSelected = colSysState.editRow
    let selected = this.row.selected

    /* leave those read to track breaking correctly */
    /* <breaktracking> */
    this.row.tableGroupHeaderBreak?.isBreak
    this.row.tableGroupFooterBreak?.isBreak
    /* </breaktracking> */

    let nav
    if (selected) {
      if (selected.showNav) {
        nav = html`
          <ovl-trowcontrol
            class="fd-table__row ovl-table-navrow"
            .props=${() => {
              return <NavProps>{
                tableDef: def,
                data: data,
                key: key,
              }
            }}
          >
          </ovl-trowcontrol>
        `
      }
    }

    //let inlineEditClass = ""
    let selectedClass = ""
    if (editSelected) {
      // also use the selected bg if an row is editet
      if (editSelected.selected) {
        //inlineEditClass = "ovl-row-inlineedit"
        selectedClass = "ovl-row-selected"
      }
    }

    if (selected) {
      if (selected.selected) {
        selectedClass = "ovl-row-selected"
      }
    }
    let rowClass = ""
    let rowTooltipMsg
    let draggable
    let fn = this.actions.app.features[def.feature]
    let fnName = HookViewRowClass
    // also display offline save errors in rowstatus
    if (data.offline && data.offline.errors[key]) {
      let msgSet = data.offline.errors[key]
      if (!rowTooltipMsg) {
        rowTooltipMsg = ""
      }
      rowClass = "ovl-row-offline--error"
      msgSet.forEach((m) => {
        rowTooltipMsg += " " + m
      })
    } else {
      if (fn?.hooks?.[fnName]) {
        let status: ViewRowClassType = await fn.hooks[fnName](<
          HookViewRowClass_Type
        >{
          rowKey: key,
          tableDef: this.row.tableDef,
          tableData: data,
          row: data.data[key],
        })
        if (status) {
          rowClass = status.className
          rowTooltipMsg = status.tooltip
          if (!rowTooltipMsg) {
            rowTooltipMsg = undefined
          }
          draggable = status.draggable === true ? true : undefined
        }
      }
    }

    let lastRow =
      key === def.uiState.lastRowKey
        ? `__lastRow ${def.uiState.tableFilterClass}`
        : ""

    this.classToSet = `fd-table__body ovl-tableview-row ${selectedClass} ${rowClass} ${lastRow}`
    let validationSummary
    if (editSelected && editSelected.selected) {
      if (def.options.edit.editType === "inline") {
        let editRowSC = html`
          <ovl-trowsc
            class="fd-table__row ovl-editrow"
            .props=${() => {
              return <EditRowSaveCancelDef>{
                tableDef: def,
                row: row,
                data: data,
                key: key,
              }
            }}
          >
          </ovl-trowsc>
        `
        let formState: SYSFormState =
          this.state.ovl.forms[def.uiState.formType][
            `${def.id}_${this.row.key}`
          ]
        if (formState) {
          if (FormHasVisibleSummaryErrors(formState)) {
            validationSummary = html`<ovl-trowvs
              class="ovl-inline-validationsummary"
              .props=${() => {
                return {
                  formState,
                  columnsCount:
                    def.uiState.columnsRenderingState.visibleInTable.length,
                }
              }}
            ></ovl-trowvs>`
          } else if (def.options.edit.reserveSpaceForInlineValidationSummary) {
            validationSummary = html`<tr
              class="ovl-inline-validationsummary __rowspacer"
            >
              <td
                colspan=${def.uiState.columnsRenderingState.visibleInTable
                  .length}
              ></td>
            </tr>`
          }
        }

        return Promise.resolve(html`
          <ovl-trowform
            tabindex="-1"
            class=${`fd-table__row ovl-table-${def.id} ovl-editform ovl-inlineeditform ovl-editform-${def.id}`}
            id=${`trow ${def.id}_${key}`}
            .props=${() => {
              return <EditRowDef>{
                tableDef: def,
                data: data,
                row: row,
                key: key,
                mode: editSelected.mode,
              }
            }}
          >
          </ovl-trowform>
          ${validationSummary} ${editRowSC}
        `)
      }
    }

    let tableGroupHeaders
    let tableGroupFooters

    if (!def.uiState.rebuild.needsRefresh && !def.uiState.currentlyAddingKey) {
      let groupHeaderBreak = this.row.tableGroupHeaderBreak
      let groupFooterBreak = this.row.tableGroupFooterBreak
      let index = this.row.index
      let isHeader =
        groupHeaderBreak?.isBreak && fn?.hooks?.[HookTableGroupHeaders]
      let isFooter =
        groupFooterBreak?.isBreak && fn?.hooks?.[HookTableGroupFooters]
      let dataFilteredAndSorted = def.uiState.dataFilteredAndSorted
      if (isHeader || isFooter) {
        _HookTableGroupDefId = def.id
        let rowKey = this.row.key
        let currentRow = data.data[this.row.key]
        let nextRowKey
        let previousRowKey
        if (index + 1 < dataFilteredAndSorted.length) {
          nextRowKey = dataFilteredAndSorted[index + 1]
        }
        if (index - 1 > -1) {
          previousRowKey = dataFilteredAndSorted[index - 1]
        }

        if (isHeader) {
          let tableGroupHeadersFootersParams = <
            HookTableGroupHeadersFooters_Type
          >{
            def,
            data,

            grouping: {
              groups: groupHeaderBreak.groups,
              fullGroupAccessStrings: groupHeaderBreak.groupAccessStrings,
              visibleColumns: def.uiState.columnsRenderingState.visibleInTable,
            },
            allGrouping: def.uiState.tableGroupHeaderBreaks,
            row: currentRow,
            previousRowKey,
            nextRowKey,
            rowKey: this.row.key,
            isFirstRow: groupHeaderBreak.isFirstRow,
            isLastRow: groupHeaderBreak.isLastRow,
            results: [],
            getTemplate: (type: GroupHeaderFooterBreaksValueType) =>
              this.getTemplate(
                type,
                JSON.parse(
                  JSON.stringify(def.uiState.groupHeaderFooterResultToSet)
                )
              ),
          }

          fn.hooks[HookTableGroupHeaders](tableGroupHeadersFootersParams)
          tableGroupHeaders = this.getHeaderFooterTemplate(
            def,
            tableGroupHeadersFootersParams.results,
            "Header"
          )
        }

        if (isFooter) {
          // // create a corretly ordered groupValues Array which could be used for totals functionality inside hook
          // let groupValues = def.options.tableGroupAggregates.groups.map((g) => {
          //   return groups[g.groupCode].groupValue
          // })
          let tableGroupHeadersFootersParams = <
            HookTableGroupHeadersFooters_Type
          >{
            def,
            data,
            grouping: {
              groups: groupFooterBreak.groups,
              fullGroupAccessStrings: groupFooterBreak.groupAccessStrings,
              visibleColumns: def.uiState.columnsRenderingState.visibleInTable,
            },
            allGrouping: def.uiState.tableGroupHeaderBreaks,
            row: currentRow,
            previousRowKey,
            nextRowKey,
            rowKey,
            isFirstRow: groupFooterBreak.isFirstRow,
            isLastRow: groupFooterBreak.isLastRow,
            results: [],
            getTemplate: (type: GroupHeaderFooterBreaksValueType) =>
              this.getTemplate(
                type,
                JSON.parse(
                  JSON.stringify(def.uiState.groupHeaderFooterResultToSet)
                )
              ),
          }
          fn.hooks[HookTableGroupFooters](tableGroupHeadersFootersParams)
          tableGroupFooters = this.getHeaderFooterTemplate(
            def,
            tableGroupHeadersFootersParams.results,
            "Footer"
          )
        }
      }
    }
    return html`
      ${tableGroupHeaders}
      <ovl-trow
        tabindex="0"
        class="fd-table__row fd-table__row--hoverable"
        title=${ifDefined(rowTooltipMsg)}
        draggable=${ifDefined(draggable)}
        data-rowkey=${key}
        .props=${() => {
          return <TableRowDataDef>{
            row: row,
            key: key,
            tableDef: def,
            thHeaderEnd: this.row.thHeaderEnd,
            intersectionObserver: this.row.intersectionObserver,
            processesToTrackForRowFnHintDisplay:
              this.row.processesToTrackForRowFnHintDisplay,
          }
        }}
      >
      </ovl-trow>
      ${nav} ${tableGroupFooters}
    `
  }
  async afterRender() {
    if (this.classToSet) {
      this.className = this.classToSet
    }
  }

  // function which gets the resulToSet info rom TableGroupHeaderFooter Hooks and returns an array of Header or Footers (TemplateResult)
  getHeaderFooterTemplate(
    def: SYSTableDef,
    results: any[],
    typ: "Header" | "Footer"
  ): TemplateResult[] {
    if (!results) {
      return undefined
    }
    let res: TemplateResult[] = []
    let colCount = 0
    results.forEach((result) => {
      // for now only cover columns Type
      if (result._type !== "columns") {
        throw new Error(
          "SYS TableGroupHeaderFooter: Only columns Type supported so far...@Todo"
        )
      }
      let cols = Object.keys(result.valueToSet).filter(
        (f) => result.valueToSet[f].value !== undefined
      )
      colCount = cols.length
      let classList: string[] = []
      classList.push("ovltg")
      classList.push(`__${typ}`)
      classList.push(`__${result._type}`)
      classList.push(result.cssClassToSet)
      let row
      switch (result._type) {
        case "columns":
          let tds: TemplateResult[] = []
          let i = 0
          while (i < def.uiState.columnsRenderingState.visibleInTable.length) {
            let v = def.uiState.columnsRenderingState.visibleInTable[i]
            let cssForTd = result.valueToSet[v].cssClass
            if (!cssForTd) {
              cssForTd = ""
            }
            let colSpan = result.valueToSet[v].colSpan
            if (
              i === 0 &&
              colCount === 1 &&
              colSpan === undefined &&
              result.valueToSet[v].value !== undefined
            ) {
              colSpan =
                def.uiState.columnsRenderingState.visibleInTable.length - i
              //def.uiState.columnsRenderingState.visibleInTable.indexOf(v)
            }
            if (colSpan === undefined) {
              colSpan = 1
            }
            i = i + colSpan
            tds.push(html`<td
              class=${`ovl-tablegroup-value fd-table__cell ${_GetAlign(
                def.columns[v].ui.align
              )} ovltvc ${this.row.thHeaderEnd[v] ? "thGrp" : ""} ${cssForTd}`}
              colspan=${colSpan}
            >
              ${result.valueToSet[v].value}
            </td>`)
          }

          row = html`<tr
            class=${`fd-table__row fd-table__row--hoverable ${classList.join(
              " "
            )}`}
          >
            ${tds}
          </tr>`
          res.push(row)
          break
      }
    })

    // // display the refresh button as well here (aggregates, groups are invalid after any changes)

    // if (
    //   res.length > 0 &&
    //   def.uiState.needsRefresh &&
    //   def.features.showRefreshButton === "TableNavAndGroupHeadersGroupFooters"
    // ) {
    //   const handleRefreshClick = (e: Event) => {
    //     e.stopPropagation()
    //     e.preventDefault()
    //     TableRebuild(def.id, { showProcess: true })
    //   }

    //   let td = html`<td
    //     class="ovl-tablegroup-refreshbutton fd-table__cell"
    //     colspan="${colCount}"
    //   >
    //     <button
    //       @click=${(e) => handleRefreshClick(e)}
    //       class="fd-button"
    //       title="Ansicht aktualisieren"
    //     >
    //       <i class="sap-icon--refresh"></i>
    //     </button>
    //   </td>`
    //   let row = html`<tr
    //     class=${`ovl-tablegroup-headerfooter ovl-tablegroup-maintype__${typ}refresh`}
    //   >
    //     ${td}
    //   </tr>`
    //   if (typ === "Header") {
    //     res.push(row)
    //   } else {
    //     res.unshift(row)
    //   }
    // }

    return res
  }
}
